import type { Ruleset } from './ruleset'

/**
 * Startpunkter för regeluppsättningar, hämtade ur v1:s Parameters-flik för
 * 2026 års majors.
 *
 * Reglerna sätts av den som kom sist i föregående major, så de ser aldrig
 * likadana ut två gånger. Presets är alltså inte facit — de är ett sätt att
 * slippa börja från tomt papper, och ska redigeras.
 */

export interface Preset {
  key: string
  label: string
  note: string
  ruleset: Ruleset
}

const STANDARD_TIEBREAKERS = ['total_to_par', 'fewest_usa', 'best_nordic', 'highest_rank_sum']

export const PRESETS: Preset[] = [
  {
    key: 'blank',
    label: 'Tomt',
    note: 'Bara lagstorlek. Bygg reglerna själv.',
    ruleset: {
      team_size: 6,
      constraints: [],
      cut_penalty: { mode: 'none', steps: [] },
      wd_penalty: { mode: 'cutline_plus', strokes: 5 },
      tiebreakers: STANDARD_TIEBREAKERS,
    },
  },
  {
    key: 'pga_2026',
    label: 'PGA 2026',
    note: 'Max 1 topp-10, rankingsumma ≥ 200, 6 olika länder. Inget cut-straff.',
    ruleset: {
      team_size: 6,
      constraints: [
        { type: 'max_top_n', params: { n: 10, max: 1 } },
        { type: 'min_rank_sum', params: { min: 200 } },
        { type: 'min_distinct_countries', params: { min: 6 } },
      ],
      cut_penalty: { mode: 'none', steps: [] },
      wd_penalty: { mode: 'cutline_plus', strokes: 5 },
      tiebreakers: STANDARD_TIEBREAKERS,
    },
  },
  {
    key: 'us_open_2026',
    label: 'US Open 2026',
    note: '2 USA + 2 Europa + 2 övriga, max 2 topp-10, ranking ≥ 250, graderat cut-straff.',
    ruleset: {
      team_size: 6,
      constraints: [
        { type: 'region_exact', params: { USA: 2, EUROPE: 2, ROW: 2 } },
        { type: 'max_top_n', params: { n: 10, max: 2 } },
        { type: 'min_rank_sum', params: { min: 250 } },
      ],
      cut_penalty: { mode: 'graded', steps: [5, 15, 30] },
      wd_penalty: { mode: 'cutline_plus', strokes: 5 },
      tiebreakers: STANDARD_TIEBREAKERS,
    },
  },
  {
    key: 'the_open_2026',
    label: 'The Open 2026',
    note: 'Fria val, max 2 topp-10, ranking ≥ 250. Inget cut-straff. Tiebreak på rankingsumma.',
    ruleset: {
      team_size: 6,
      constraints: [
        { type: 'max_top_n', params: { n: 10, max: 2 } },
        { type: 'min_rank_sum', params: { min: 250 } },
      ],
      cut_penalty: { mode: 'none', steps: [] },
      wd_penalty: { mode: 'cutline_plus', strokes: 5 },
      tiebreakers: ['highest_rank_sum', 'total_to_par'],
    },
  },
]

/** Cut-position per major. Avgör den projicerade cut-linjen före cutten. */
export const CUT_POSITION: Record<string, number> = {
  masters: 50,
  pga: 70,
  us_open: 60,
  the_open: 70,
}
